import { useHasPermission, useUTLSettingsContext } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { useMemo } from "react";
import { CurrentDelayData } from "../models/CurrentDelayData";
import { DelayCodeFilterMode } from "@/shared/selectors/DelayCodeTreePicker";

// Permission action names (must match database SYSTEM.catActions)
const PERMISSION_UNDECLARED = "undeclaredstop_showDelaysPopup";

interface UsePassingTheBallOptions {
	delayData: Partial<CurrentDelayData> | null | undefined;
	/**
	 * Optional: Override branchCodeId for cases where delayData.lastDelayCodeId
	 * is available but not the branch code ID itself.
	 */
	overrideBranchCodeId?: number | null;
}

interface UsePassingTheBallResult {
	/** Feature flag is enabled */
	isFeatureEnabled: boolean;
	/** User has permission to see undeclared delays popup */
	canSeeUndeclaredPopup: boolean;
	/** User has permission to see this specific branch popup */
	canSeeBranchPopup: boolean;
	/** Should show popup based on current delay state and permissions */
	shouldShowPopup: boolean;
	/** The type of popup to show: 'undeclared' or 'branch' */
	popupType: "undeclared" | "branch" | null;
	/** Filter mode for delay code selection: what codes the user can see */
	filterMode: DelayCodeFilterMode;
	/** Branch code ID when filterMode is "children-of" (specialist mode) */
	branchCodeId: number | null;
}

function validateBoolean(value: unknown): boolean {
	if (typeof value === "boolean") return value;
	if (typeof value === "string") {
		const lower = value.toLowerCase();
		if (lower === "true" || lower === "1" || lower === "yes") return true;
		if (lower === "false" || lower === "0" || lower === "no") return false;
	}
	if (typeof value === "number") return !!value;
	return false;
}

/**
 * Hook for "Passing the Ball" workflow logic.
 *
 * Determines if the current user should see a delay declaration popup based on:
 * 1. Feature flag (FF_PASSING_THE_BALL_ENABLED)
 * 2. Current delay state (active, declared, branch code)
 * 3. User permissions (undeclared popup, branch-specific popup)
 *
 * @example
 * const { shouldShowPopup, popupType } = usePassingTheBall({ delayData });
 * if (shouldShowPopup) {
 *   // Show declaration modal
 * }
 */
export const usePassingTheBall = ({
	delayData,
	overrideBranchCodeId,
}: UsePassingTheBallOptions): UsePassingTheBallResult => {
	const hasPermissionTo = useHasPermission();
	const {
		state: { settings },
	} = useUTLSettingsContext();

	// Check feature flag
	const isFeatureEnabled = useMemo(() => {
		return validateBoolean(get(settings, "FF_PASSING_THE_BALL_ENABLED", false));
	}, [settings]);

	// Check if user can see undeclared delays popup (general operator)
	const canSeeUndeclaredPopup = useMemo(() => {
		return hasPermissionTo(PERMISSION_UNDECLARED);
	}, [hasPermissionTo]);

	// Check if user can see branch-specific popup (specialist)
	const canSeeBranchPopup = useMemo(() => {
		if (!delayData?.branchPermissionAction) return false;
		return hasPermissionTo(delayData.branchPermissionAction);
	}, [hasPermissionTo, delayData?.branchPermissionAction]);

	// Determine if popup should be shown and what type
	const { shouldShowPopup, popupType } = useMemo(() => {
		// Feature must be enabled
		if (!isFeatureEnabled || !delayData) {
			return { shouldShowPopup: false, popupType: null };
		}

		// Must have an active delay (End is null)
		if (!delayData.delayActive) {
			return { shouldShowPopup: false, popupType: null };
		}

		// Case 1: Delay is NOT declared yet (CodeId is null)
		// Show popup to general operators with undeclared permission
		if (!delayData.delayDeclared) {
			if (canSeeUndeclaredPopup) {
				return { shouldShowPopup: true, popupType: "undeclared" as const };
			}
			return { shouldShowPopup: false, popupType: null };
		}

		// Case 2: Delay IS declared with a branch code
		// Show popup to specialists who have permission for this branch
		if (delayData.isBranchCode && delayData.branchPermissionAction) {
			if (canSeeBranchPopup) {
				return { shouldShowPopup: true, popupType: "branch" as const };
			}
			return { shouldShowPopup: false, popupType: null };
		}

		// Case 3: Delay is declared with a specific code (not a branch)
		// No popup needed - classification is complete
		return { shouldShowPopup: false, popupType: null };
	}, [isFeatureEnabled, delayData, canSeeUndeclaredPopup, canSeeBranchPopup]);

	// Determine filter mode for delay code selection (RESTRICTIVE approach)
	const { filterMode, branchCodeId } = useMemo((): {
		filterMode: DelayCodeFilterMode;
		branchCodeId: number | null;
	} => {
		// If feature is disabled, show all codes (legacy behavior)
		if (!isFeatureEnabled) {
			return { filterMode: "all", branchCodeId: null };
		}

		// If popup should NOT show, use "all" mode (user is browsing, not being prompted)
		if (!shouldShowPopup) {
			return { filterMode: "all", branchCodeId: null };
		}

		// Undeclared popup -> Operator mode: can only select branch codes
		if (popupType === "undeclared") {
			return { filterMode: "branches-only", branchCodeId: null };
		}

		// Branch popup -> Specialist mode: can only select children of the branch
		if (popupType === "branch") {
			// Use override if provided, otherwise try to get from lastDelayCodeId
			const effectiveBranchCodeId =
				overrideBranchCodeId ?? delayData?.lastDelayCodeId ?? null;
			return {
				filterMode: "children-of",
				branchCodeId: effectiveBranchCodeId,
			};
		}

		// Default: show all codes
		return { filterMode: "all", branchCodeId: null };
	}, [
		isFeatureEnabled,
		shouldShowPopup,
		popupType,
		delayData?.lastDelayCodeId,
		overrideBranchCodeId,
	]);

	return {
		isFeatureEnabled,
		canSeeUndeclaredPopup,
		canSeeBranchPopup,
		shouldShowPopup,
		popupType,
		filterMode,
		branchCodeId,
	};
};
